import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './ApplicationPeriodsManagement.css';

const API_BASE_URL = `${Base_Url}/api`;

const ApplicationPeriodsManagement = () => {
  const { showSuccess, showError } = useToast();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    description: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/application-periods`);
      const data = await response.json();
      
      if (data.success) {
        setPeriods(Array.isArray(data.data) ? data.data : []);
      } else {
        showError('Failed to fetch application periods');
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
      showError('Error fetching application periods');
    } finally {
      setLoading(false);
    }
  };

  const filteredPeriods = periods.filter(period => {
    const matchesSearch = period.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         period.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || period.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && new Date(formData.start_date) >= new Date(formData.end_date)) {
      errors.end_date = 'End date must be after start date';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPeriod = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/application-periods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Application period created successfully');
        setShowAddModal(false);
        resetForm();
        fetchPeriods();
      } else {
        showError(data.message || 'Failed to create application period');
      }
    } catch (error) {
      console.error('Error creating period:', error);
      showError('Error creating application period');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPeriod = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/application-periods/${selectedPeriod.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Application period updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchPeriods();
      } else {
        showError(data.message || 'Failed to update application period');
      }
    } catch (error) {
      console.error('Error updating period:', error);
      showError('Error updating application period');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePeriod = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/application-periods/${selectedPeriod.id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Application period deleted successfully');
        setShowDeleteModal(false);
        fetchPeriods();
      } else {
        showError(data.message || 'Failed to delete application period');
      }
    } catch (error) {
      console.error('Error deleting period:', error);
      showError('Error deleting application period');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (period) => {
    setSelectedPeriod(period);
    setFormData({
      title: period.title,
      start_date: period.start_date,
      end_date: period.end_date,
      description: period.description || '',
      status: period.status
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openDeleteModal = (period) => {
    setSelectedPeriod(period);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      start_date: '',
      end_date: '',
      description: '',
      status: 'active'
    });
    setFormErrors({});
    setSelectedPeriod(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'status-active', icon: <CheckCircle size={16} /> },
      inactive: { label: 'Inactive', className: 'status-inactive', icon: <XCircle size={16} /> },
      completed: { label: 'Completed', className: 'status-completed', icon: <Clock size={16} /> }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    
    return (
      <span className={`status-badge ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="periods-management">
        <div className="loading-spinner">Loading application periods...</div>
      </div>
    );
  }

  return (
    <div className="periods-management">
      <div className="management-header">
        <h2>Application Periods Management</h2>
        <button 
          className="add-period-btn"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <Plus size={20} />
          Add New Period
        </button>
      </div>

      {/* Search and Filter */}
      <div className="search-filter-container">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search periods..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <Filter size={20} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Periods Table */}
      <div className="periods-table-container">
        <table className="periods-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPeriods.map(period => (
              <tr key={period.id}>
                <td>
                  <div className="period-title">
                    <h4>{period.title}</h4>
                    {period.description && (
                      <p className="period-description">{period.description}</p>
                    )}
                  </div>
                </td>
                <td>{formatDate(period.start_date)}</td>
                <td>{formatDate(period.end_date)}</td>
                <td>{getStatusBadge(period.status)}</td>
                <td>{formatDate(period.created_at)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => openEditModal(period)}
                      title="Edit Period"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => openDeleteModal(period)}
                      title="Delete Period"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredPeriods.length === 0 && (
          <div className="no-data">
            <p>No application periods found</p>
          </div>
        )}
      </div>

      {/* Add Period Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Application Period</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={formErrors.title ? 'error' : ''}
                />
                {formErrors.title && <span className="error-message">{formErrors.title}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={formErrors.start_date ? 'error' : ''}
                  />
                  {formErrors.start_date && <span className="error-message">{formErrors.start_date}</span>}
                </div>
                
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className={formErrors.end_date ? 'error' : ''}
                  />
                  {formErrors.end_date && <span className="error-message">{formErrors.end_date}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddPeriod}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Period Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Application Period</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={formErrors.title ? 'error' : ''}
                />
                {formErrors.title && <span className="error-message">{formErrors.title}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={formErrors.start_date ? 'error' : ''}
                  />
                  {formErrors.start_date && <span className="error-message">{formErrors.start_date}</span>}
                </div>
                
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className={formErrors.end_date ? 'error' : ''}
                  />
                  {formErrors.end_date && <span className="error-message">{formErrors.end_date}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleEditPeriod}
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Period'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Application Period</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete the application period "<strong>{selectedPeriod?.title}</strong>"?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger" 
                onClick={handleDeletePeriod}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationPeriodsManagement; 